import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getStudent, getStudentSummary, Student, StudentSummary } from "../api/client";
import api from "../api/client";

interface Meeting { id: string; date: string; attendees: string; notes: string; }
interface FollowUp { id: string; action: string; status: string; due_date: string; notes: string; created_at: string; }
interface QA { id: string; question: string; response: string; asked_date: string; }

export default function StudentDetail() {
  const { id } = useParams<{ id: string }>();
  const [student, setStudent] = useState<Student | null>(null);
  const [summary, setSummary] = useState<StudentSummary | null>(null);
  const [tab, setTab] = useState<"meetings" | "followups" | "qa">("meetings");

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [followups, setFollowups] = useState<FollowUp[]>([]);
  const [qas, setQas] = useState<QA[]>([]);

  // Forms
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ date: new Date().toISOString().split("T")[0], attendees: "", notes: "" });
  const [showFUForm, setShowFUForm] = useState(false);
  const [fuForm, setFuForm] = useState({ action: "", due_date: "", notes: "" });
  const [showQAForm, setShowQAForm] = useState(false);
  const [qaForm, setQaForm] = useState({ question: "", response: "", asked_date: new Date().toISOString().split("T")[0] });

  const load = () => {
    if (!id) return;
    getStudent(id).then((r) => setStudent(r.data));
    getStudentSummary(id).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/fruit/${id}/meetings`).then((r) => setMeetings(r.data));
    api.get(`/fruit/${id}/followups`).then((r) => setFollowups(r.data));
    api.get(`/fruit/${id}/qa`).then((r) => setQas(r.data));
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!student) return <div className="empty">Loading...</div>;

  return (
    <div>
      <div className="detail-header">
        <div>
          <Link to="/fruit-status" style={{ color: "var(--text-muted)", fontSize: 13 }}>&larr; Back to Fruit Status</Link>
          <h2>{student.name}</h2>
          <div style={{ color: "var(--text-muted)", fontSize: 14 }}>
            {student.phone && <span>{student.phone} &middot; </span>}
            {student.email && <span>{student.email} &middot; </span>}
            Classes: {student.classes?.map((c) => c.name).join(", ") || "None"}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="summary-grid">
        <div className="stat-card">
          <div className="label">Sessions</div>
          <div className="value">{summary?.total_sessions ?? "-"}</div>
        </div>
        <div className="stat-card">
          <div className="label">Attendance</div>
          <div className="value">{summary?.attendance_rate ?? "-"}%</div>
        </div>
        <div className="stat-card">
          <div className="label">HW Completion</div>
          <div className="value">{summary?.completion_rate ?? "-"}%</div>
        </div>
        <div className="stat-card">
          <div className="label">Meetings</div>
          <div className="value">{meetings.length}</div>
        </div>
        <div className="stat-card">
          <div className="label">Pending F/U</div>
          <div className="value">{followups.filter((f) => f.status === "pending").length}</div>
        </div>
      </div>

      <div className="tab-bar">
        <button className={tab === "meetings" ? "active" : ""} onClick={() => setTab("meetings")}>Meetings ({meetings.length})</button>
        <button className={tab === "followups" ? "active" : ""} onClick={() => setTab("followups")}>Follow-ups ({followups.length})</button>
        <button className={tab === "qa" ? "active" : ""} onClick={() => setTab("qa")}>Q&A ({qas.length})</button>
      </div>

      {/* === MEETINGS === */}
      {tab === "meetings" && (
        <>
          <div className="toolbar">
            <span></span>
            <button className="btn btn-primary btn-sm" onClick={() => setShowMeetingForm(true)}>+ Add Meeting</button>
          </div>
          <div className="table-container">
            {meetings.length === 0 ? (
              <div className="empty">No meetings recorded</div>
            ) : (
              <table>
                <thead><tr><th>Date</th><th>Attendees</th><th>Notes</th><th></th></tr></thead>
                <tbody>
                  {meetings.map((m) => (
                    <tr key={m.id}>
                      <td style={{ whiteSpace: "nowrap" }}>{m.date}</td>
                      <td style={{ fontSize: 13 }}>{m.attendees || "-"}</td>
                      <td style={{ fontSize: 13, maxWidth: 400 }}>{m.notes || "-"}</td>
                      <td>
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                          await api.delete(`/fruit/${id}/meetings/${m.id}`);
                          load();
                        }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {showMeetingForm && (
            <div className="modal-overlay" onClick={() => setShowMeetingForm(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Add Meeting</h3>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={meetingForm.date} onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Attendees</label>
                  <input value={meetingForm.attendees} onChange={(e) => setMeetingForm({ ...meetingForm, attendees: e.target.value })} placeholder="Who attended" />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea value={meetingForm.notes} onChange={(e) => setMeetingForm({ ...meetingForm, notes: e.target.value })} placeholder="What was discussed, key takeaways..." />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowMeetingForm(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={async () => {
                    await api.post(`/fruit/${id}/meetings`, meetingForm);
                    setShowMeetingForm(false);
                    setMeetingForm({ date: new Date().toISOString().split("T")[0], attendees: "", notes: "" });
                    load();
                  }}>Add</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* === FOLLOW-UPS === */}
      {tab === "followups" && (
        <>
          <div className="toolbar">
            <span></span>
            <button className="btn btn-primary btn-sm" onClick={() => setShowFUForm(true)}>+ Add Follow-up</button>
          </div>
          <div className="table-container">
            {followups.length === 0 ? (
              <div className="empty">No follow-ups</div>
            ) : (
              <table>
                <thead><tr><th>Action</th><th>Due</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {followups.map((f) => (
                    <tr key={f.id}>
                      <td style={{ fontSize: 13 }}>{f.action}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{f.due_date || "-"}</td>
                      <td>
                        <span className={`badge ${f.status}`}>{f.status}</span>
                      </td>
                      <td style={{ display: "flex", gap: 4 }}>
                        {f.status === "pending" && (
                          <button className="btn btn-secondary btn-sm" onClick={async () => {
                            await api.patch(`/fruit/${id}/followups/${f.id}`, { status: "resolved" });
                            load();
                          }}>Resolve</button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                          await api.delete(`/fruit/${id}/followups/${f.id}`);
                          load();
                        }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {showFUForm && (
            <div className="modal-overlay" onClick={() => setShowFUForm(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Add Follow-up</h3>
                <div className="form-group">
                  <label>Action *</label>
                  <input value={fuForm.action} onChange={(e) => setFuForm({ ...fuForm, action: e.target.value })} placeholder="What needs to be done" />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" value={fuForm.due_date} onChange={(e) => setFuForm({ ...fuForm, due_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea value={fuForm.notes} onChange={(e) => setFuForm({ ...fuForm, notes: e.target.value })} />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowFUForm(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={async () => {
                    await api.post(`/fruit/${id}/followups`, fuForm);
                    setShowFUForm(false);
                    setFuForm({ action: "", due_date: "", notes: "" });
                    load();
                  }}>Add</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* === Q&A === */}
      {tab === "qa" && (
        <>
          <div className="toolbar">
            <span></span>
            <button className="btn btn-primary btn-sm" onClick={() => setShowQAForm(true)}>+ Add Q&A</button>
          </div>
          <div className="table-container">
            {qas.length === 0 ? (
              <div className="empty">No questions recorded</div>
            ) : (
              <table>
                <thead><tr><th>Date</th><th>Question</th><th>Response</th><th></th></tr></thead>
                <tbody>
                  {qas.map((q) => (
                    <tr key={q.id}>
                      <td style={{ whiteSpace: "nowrap" }}>{q.asked_date || "-"}</td>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{q.question}</td>
                      <td style={{ fontSize: 13 }}>{q.response || <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>No response yet</span>}</td>
                      <td>
                        <button className="btn btn-danger btn-sm" onClick={async () => {
                          await api.delete(`/fruit/${id}/qa/${q.id}`);
                          load();
                        }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {showQAForm && (
            <div className="modal-overlay" onClick={() => setShowQAForm(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Add Q&A</h3>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={qaForm.asked_date} onChange={(e) => setQaForm({ ...qaForm, asked_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Question *</label>
                  <textarea value={qaForm.question} onChange={(e) => setQaForm({ ...qaForm, question: e.target.value })} placeholder="What did the fruit ask?" />
                </div>
                <div className="form-group">
                  <label>Response</label>
                  <textarea value={qaForm.response} onChange={(e) => setQaForm({ ...qaForm, response: e.target.value })} placeholder="How was it answered?" />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowQAForm(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={async () => {
                    await api.post(`/fruit/${id}/qa`, qaForm);
                    setShowQAForm(false);
                    setQaForm({ question: "", response: "", asked_date: new Date().toISOString().split("T")[0] });
                    load();
                  }}>Add</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
